/**
 * Resolve a human-readable label for usage click/input events.
 * Walks up from the event target (often an icon/SVG) to the interactive control.
 * Never returns form field values.
 */

const INTERACTIVE_SELECTOR = [
  'button',
  'a[href]',
  'input',
  'textarea',
  'select',
  'label',
  'summary',
  '[role="button"]',
  '[role="link"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '.v-btn',
  '.v-list-item',
  '.v-chip',
  '.v-tab',
  '.v-checkbox',
  '.v-switch',
  '.v-radio',
  '[data-testid]',
].join(',')

const NOISE_RE = /^(mdi-|v-icon|v-btn__overlay|v-btn__underlay|v-ripple)/i

function cleanText(s, max = 100) {
  return String(s || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max)
}

function associatedLabelText(el) {
  if (!el || typeof document === 'undefined') return ''
  const id = el.getAttribute?.('id')
  if (id) {
    const safe =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(id)
        : String(id).replace(/\\/g, '\\\\').replace(/"/g, '\\"')
    const byFor = document.querySelector(`label[for="${safe}"]`)
    if (byFor) return cleanText(byFor.innerText || byFor.textContent)
  }
  const wrap = el.closest?.('label')
  if (wrap) return cleanText(wrap.innerText || wrap.textContent)
  // Vuetify field: label sits in .v-label / .v-field-label within the same field.
  const field = el.closest?.('.v-input, .v-field, .v-text-field, .v-select, .v-textarea')
  if (field) {
    const lab = field.querySelector?.('.v-label, .v-field-label, label')
    if (lab) return cleanText(lab.innerText || lab.textContent)
  }
  return ''
}

function visibleControlText(el) {
  if (!el) return ''
  // Prefer Vuetify button/list content slots (skip icons).
  const slot =
    el.querySelector?.('.v-btn__content, .v-list-item-title, .v-chip__content, .v-tab__content') ||
    el
  const clone = slot.cloneNode?.(true)
  if (clone) {
    clone.querySelectorAll?.('i, svg, .v-icon, .mdi').forEach((n) => n.remove())
    const t = cleanText(clone.innerText || clone.textContent)
    if (t) return t
  }
  return cleanText(el.innerText || el.textContent)
}

/**
 * @param {Element|null|undefined} start
 * @returns {Element|null}
 */
export function resolveUsageControl(start) {
  if (!start || !(start instanceof Element)) return null
  if (start.closest) {
    const hit = start.closest(INTERACTIVE_SELECTOR)
    if (hit) return hit
  }
  let node = start
  for (let i = 0; i < 8 && node; i += 1) {
    if (node instanceof Element && node.matches?.(INTERACTIVE_SELECTOR)) return node
    node = node.parentElement
  }
  return start instanceof Element ? start : null
}

/**
 * @param {Element|null|undefined} el
 * @returns {string}
 */
export function usageEventLabel(el) {
  const control = resolveUsageControl(el)
  if (!control) return ''

  const aria = cleanText(control.getAttribute?.('aria-label'))
  if (aria) return aria

  const titled = cleanText(control.getAttribute?.('title'))
  if (titled) return titled

  const placeholder = cleanText(control.getAttribute?.('placeholder'))
  if (placeholder) return placeholder

  const assoc = associatedLabelText(control)
  if (assoc) return assoc

  const nameAttr = cleanText(control.getAttribute?.('name'))
  if (nameAttr && /^(INPUT|TEXTAREA|SELECT)$/i.test(control.tagName)) return nameAttr

  const text = visibleControlText(control)
  if (text && !NOISE_RE.test(text)) return text

  const testId = cleanText(control.getAttribute?.('data-testid'))
  if (testId) return testId

  const href = cleanText(control.getAttribute?.('href'))
  if (href && href !== '#') return href.slice(0, 80)

  const tag = (control.tagName || '').toLowerCase()
  const role = cleanText(control.getAttribute?.('role'))
  if (role) return `${tag || 'el'}[${role}]`
  return tag || ''
}
